import React from 'react';
import { Input, Button } from '../../../../components/ui';
import { ClientRecord } from '../hooks/useProjects';

type ClientSelectionMode = 'hidden' | 'required' | 'optional';
type FormMode = 'create' | 'edit';

export interface ProjectFormData {
  name: string;
  description?: string;
  infrastructureBudget: string | number;
  deliveryBudget: string | number;
  startDate?: string;
  endDate?: string;
  clientId?: string;
}

interface Props {
  formData: ProjectFormData;
  onChange: (data: ProjectFormData) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
  clients?: ClientRecord[];
  clientsLoading?: boolean;
  loading?: boolean;
  mode?: FormMode;
  onCreateClient?: () => void;
  /**
   * When 'hidden', client selector is not shown and clientId is not required
   * (partner/client get client from context). When 'required' or 'optional',
   * client selector is shown; in create mode 'required' makes clientId mandatory.
   */
  clientSelectionMode?: ClientSelectionMode;
}

export const ProjectForm: React.FC<Props> = ({
  formData,
  onChange,
  onSubmit,
  onCancel,
  clients = [],
  clientsLoading = false,
  loading = false,
  mode = 'create',
  onCreateClient,
  clientSelectionMode = 'required',
}) => {
  const handleInputChange = (field: keyof ProjectFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    onChange({
      ...formData,
      [field]: e.target.value
    });
  };

  const showClientSelector = clientSelectionMode !== 'hidden';
  const clientRequired = mode === 'create' && clientSelectionMode === 'required';

  const isFormValid = (): boolean => {
    if (mode === 'create') {
      return Boolean(
        formData.name?.trim() &&
        (!clientRequired || formData.clientId) &&
        formData.infrastructureBudget &&
        formData.deliveryBudget &&
        formData.startDate &&
        formData.endDate
      );
    }
    return Boolean(formData.name?.trim());
  };

  return (
    <form onSubmit={onSubmit}>
      <Input
        label="Project Name"
        value={formData.name}
        onChange={handleInputChange('name')}
        placeholder="Enter project name"
        required
        className="mb-6"
      />

      <div className="mb-6">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea id="description"
          value={formData.description ?? ''}
          onChange={handleInputChange('description')}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
          placeholder="Enter project description"
        />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <Input
          label="Infrastructure Budget"
          type="number"
          value={String(formData.infrastructureBudget)}
          onChange={handleInputChange('infrastructureBudget')}
          placeholder="0.00"
          step="0.01"
          required={mode === 'create'}
        />

        <Input
          label="Delivery Budget"
          type="number"
          value={String(formData.deliveryBudget)}
          onChange={handleInputChange('deliveryBudget')}
          placeholder="0.00"
          step="0.01"
          required={mode === 'create'}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <Input
          label="Start Date"
          type="date"
          value={formData.startDate ?? ''}
          onChange={handleInputChange('startDate')}
          required={mode === 'create'}
        />

        <Input
          label="End Date"
          type="date"
          value={formData.endDate ?? ''}
          onChange={handleInputChange('endDate')}
          required={mode === 'create'}
        />
      </div>

      {showClientSelector && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Client {clientRequired && <span className="text-red-500">*</span>}
            </span>
            {onCreateClient && (
              <button
                type="button"
                onClick={onCreateClient}
                className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
              >
                + New Client
              </button>
            )}
          </div>

          {clients.length > 0 ? (
            <select
              value={formData.clientId ?? ''}
              onChange={handleInputChange('clientId')}
              disabled={clientsLoading}
              required={clientRequired}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Select a client</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name} ({client.email})
                </option>
              ))}
            </select>
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400 p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
              {clientsLoading ? (
                'Loading clients...'
              ) : (
                <span>
                  No clients available.{' '}
                  <button
                    type="button"
                    onClick={onCreateClient}
                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium underline"
                  >
                    Create your first client
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <Button type="button" onClick={onCancel} variant="secondary">
          Cancel
        </Button>
        <Button type="submit" loading={loading} disabled={!isFormValid() || loading}>
          {mode === 'create' ? 'Create Project' : 'Update Project'}
        </Button>
      </div>
    </form>
  );
};

export default ProjectForm;
